import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";

function randInt(min, max, exclude = []) {
  let n;
  do {
    n = Math.floor(Math.random() * (max - min + 1)) + min;
  } while (exclude.includes(n));
  return n;
}

function choice(items) {
  return items[Math.floor(Math.random() * items.length)];
}

function quadraticValue(a, b, c, x) {
  return a * x * x + b * x + c;
}

function lineLatexFromUser(aParsed, aRaw, bParsed, bRaw) {
  if (!Number.isFinite(aParsed) || !Number.isFinite(bParsed)) return null;

  const aLatexRaw = String(formatLatexPreview(aRaw || ""));
  const bLatexRaw = String(formatLatexPreview(bRaw || ""));
  const aLatex = aLatexRaw.includes("=") ? aLatexRaw.split("=")[1].trim() : aLatexRaw;
  const bLatex = bLatexRaw.includes("=") ? bLatexRaw.split("=")[1].trim() : bLatexRaw;

  if (aParsed === 0) {
    return `y = ${bLatex}`;
  }

  const aPart = aParsed === 1 ? "x" : aParsed === -1 ? "-x" : `${aLatex}x`;
  if (bParsed === 0) return `y = ${aPart}`;
  return `y = ${aPart} ${bParsed >= 0 ? "+" : "-"} ${bLatex.replace("-", "")}`;
}

function lineLatex(m, d) {
  if (m === 0) return `${d}`;
  const mPart = m === 1 ? "x" : m === -1 ? "-x" : `${m}x`;
  if (d === 0) return mPart;
  return d > 0 ? `${mPart} + ${d}` : `${mPart} - ${Math.abs(d)}`;
}

function quadraticLatex(a, b, c) {
  const parts = [];
  const aPart = a === 1 ? "x^2" : a === -1 ? "-x^2" : `${a}x^2`;
  parts.push(aPart);
  if (b !== 0) {
    const bPart = Math.abs(b) === 1 ? "x" : `${Math.abs(b)}x`;
    parts.push(b > 0 ? `+ ${bPart}` : `- ${bPart}`);
  }
  if (c !== 0) {
    parts.push(c > 0 ? `+ ${c}` : `- ${Math.abs(c)}`);
  }
  return parts.join(" ");
}

function weightedX0() {
  const outer = [-4, -3, 3, 4];
  const inner = [-2, -1, 0, 1, 2];
  return Math.random() < 0.82 ? choice(outer) : choice(inner);
}

function buildWindow(center, span = 10) {
  let start = Math.round(center) - span / 2;
  let end = start + span;

  // Jesli da sie to pogodzic, trzymaj 0 w kadrze przy zachowaniu stalego rozmiaru.
  if (start > 0) {
    end -= start;
    start = 0;
  } else if (end < 0) {
    start -= end;
    end = 0;
  }

  return [Math.round(start), Math.round(end)];
}

function buildRanges(x0, y0) {
  return {
    xRange: buildWindow(x0),
    yRange: buildWindow(y0),
  };
}

function generateCoefficients() {
  for (let attempt = 0; attempt < 800; attempt += 1) {
    const x0 = weightedX0();
    const y0 = randInt(-4, 4);
    const a = randInt(-2, 2, [0]);
    const m = randInt(-5, 5, [0]);
    const b = m - 2 * a * x0;
    const c = y0 - a * x0 * x0 - b * x0;
    const d = y0 - m * x0;
    const { xRange, yRange } = buildRanges(x0, y0);

    const tangentReasonable = Math.abs(m) <= 5 && d >= yRange[0] - 2 && d <= yRange[1] + 2;
    const coefficientsReasonable = Math.abs(b) <= 14 && Math.abs(c) <= 24;
    const localParabolaVisible = [x0 - 1, x0, x0 + 1]
      .filter((x) => x >= xRange[0] && x <= xRange[1])
      .every((x) => {
        const y = quadraticValue(a, b, c, x);
        return y >= yRange[0] && y <= yRange[1];
      });

    if (tangentReasonable && coefficientsReasonable && localParabolaVisible) {
      return { a, b, c, x0, y0, m, d, xRange, yRange };
    }
  }

  const x0 = 4;
  const y0 = 1;
  const a = 1;
  const m = 4;
  const b = m - 2 * a * x0;
  const c = y0 - a * x0 * x0 - b * x0;
  const d = y0 - m * x0;
  const { xRange, yRange } = buildRanges(x0, y0);
  return { a, b, c, x0, y0, m, d, xRange, yRange };
}

function quadraticExpr(a, b, c) {
  const parts = [`${a}x^2`];
  if (b !== 0) parts.push(b > 0 ? `+${b}x` : `${b}x`);
  if (c !== 0) parts.push(c > 0 ? `+${c}` : `${c}`);
  return `y=${parts.join("")}`;
}

function lineExpr(m, d) {
  if (m === 0) return `y=${d}`;
  const parts = [`${m}x`];
  if (d !== 0) parts.push(d > 0 ? `+${d}` : `${d}`);
  return `y=${parts.join("")}`;
}

export function generateTask() {
  const { a, b, c, x0, y0, m, d, xRange, yRange } = generateCoefficients();

  return {
    type: "QuadraticTangentTask",
    prompt: `Wyznacz rownanie stycznej do wykresu funkcji f(x) = ${quadraticLatex(a, b, c)} w punkcie P = (${x0}, ${y0}). Podaj wspolczynniki a i b prostej y = ax + b.`,
    answer: `a=${m}, b=${d}`,
    data: {
      qa: a,
      qb: b,
      qc: c,
      x0,
      y0,
      tangentA: m,
      tangentB: d,
      functionLatex: quadraticLatex(a, b, c),
      tangentLatex: lineLatex(m, d),
      xRange,
      yRange,
    },
  };
}

export function renderTask({ data }) {
  return (
    <div className="space-y-2">
      <p>Wyznacz rownanie stycznej do wykresu funkcji:</p>
      <BlockMath math={`f(x) = ${data.functionLatex}`} />
      <p>w punkcie <InlineMath math={`P = (${data.x0}, ${data.y0})`} />.</p>
      <p>Podaj wspolczynniki <InlineMath math={"a"} /> i <InlineMath math={"b"} /> prostej <InlineMath math={"y=ax+b"} />.</p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const value = userAnswer && typeof userAnswer === "object" ? userAnswer : {};
  return (
    <div className="space-y-3">
      <div>
        <div style={{ marginBottom: "0.35rem" }}><InlineMath math="a =" /></div>
        <FractionInput
          value={value.answerA || ""}
          onChange={(next) => onChange({ ...value, answerA: next })}
          disabled={disabled}
          label="a"
        />
      </div>
      <div>
        <div style={{ marginBottom: "0.35rem" }}><InlineMath math="b =" /></div>
        <FractionInput
          value={value.answerB || ""}
          onChange={(next) => onChange({ ...value, answerB: next })}
          disabled={disabled}
          label="b"
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;
  const aValue = userAnswer.answerA?.parsed;
  const bValue = userAnswer.answerB?.parsed;
  return typeof aValue === "number" && typeof bValue === "number"
    && Math.abs(aValue - data.tangentA) < 0.0001
    && Math.abs(bValue - data.tangentB) < 0.0001;
}

export function renderUserAnswer(_taskOrAnswer, maybeUserAnswer) {
  const userAnswer = maybeUserAnswer !== undefined ? maybeUserAnswer : _taskOrAnswer;
  if (!userAnswer || typeof userAnswer !== "object") return <i>(brak odpowiedzi)</i>;
  const rawA = userAnswer.answerA?.raw;
  const rawB = userAnswer.answerB?.raw;
  if (!rawA && !rawB) return <i>(brak odpowiedzi)</i>;
  const aValue = userAnswer.answerA?.parsed;
  const bValue = userAnswer.answerB?.parsed;
  const userLineLatex = lineLatexFromUser(aValue, rawA, bValue, rawB);
  return (
    <div className="space-y-2">
      {rawA ? <div><InlineMath math={formatLatexPreview(rawA, "a")} /></div> : <div><InlineMath math={"a = ?"} /></div>}
      {rawB ? <div><InlineMath math={formatLatexPreview(rawB, "b")} /></div> : <div><InlineMath math={"b = ?"} /></div>}
      {userLineLatex ? (
        <div style={{ marginTop: "0.25rem" }}>
          <InlineMath math={userLineLatex} />
        </div>
      ) : null}
    </div>
  );
}

export function renderAnswer({ data }) {
  return (
    <div className="space-y-1">
      <div><InlineMath math={`a = ${data.tangentA}`} /></div>
      <div><InlineMath math={`b = ${data.tangentB}`} /></div>
      <div><InlineMath math={`y = ${data.tangentLatex}`} /></div>
    </div>
  );
}

export function renderExplanation({ data }) {
  const derivativeLatex = `${2 * data.qa}x${data.qb === 0 ? "" : data.qb > 0 ? ` + ${data.qb}` : ` - ${Math.abs(data.qb)}`}`;
  const slopeLatex = `${2 * data.qa}\\cdot ${data.x0}${data.qb >= 0 ? ` + ${data.qb}` : ` - ${Math.abs(data.qb)}`}`;
  const parabolaExpr = quadraticExpr(data.qa, data.qb, data.qc);
  const tangentExpr = lineExpr(data.tangentA, data.tangentB);

  return (
    <div className="space-y-2" style={{ textAlign: "left" }}>
      <p><strong>Krok 1:</strong> Liczymy pochodna funkcji kwadratowej.</p>
      <BlockMath math={`f(x) = ${data.functionLatex}`} />
      <BlockMath math={`f'(x) = ${derivativeLatex}`} />
      <p><strong>Krok 2:</strong> Wspolczynnik kierunkowy stycznej to <InlineMath math={"f'(x_0)"} />.</p>
      <BlockMath math={`f'(${data.x0}) = ${slopeLatex} = ${data.tangentA}`} />
      <p><strong>Krok 3:</strong> Korzystamy z podanego punktu stycznosci <InlineMath math={`P(${data.x0}, ${data.y0})`} />.</p>
      <p><strong>Krok 4:</strong> Styczna ma postac <InlineMath math={"y = ax + b"} />, wiec podstawiamy <InlineMath math={`a = ${data.tangentA}`} /> i punkt <InlineMath math={`P(${data.x0}, ${data.y0})`} />.</p>
      <BlockMath math={`${data.y0} = ${data.tangentA}\\cdot ${data.x0} + b`} />
      <BlockMath math={`b = ${data.tangentB}`} />
      <p><strong>Krok 5:</strong> Zapisujemy rownanie stycznej.</p>
      <BlockMath math={`y = ${data.tangentLatex}`} />
      <div style={{ maxWidth: 350, margin: "24px auto 0" }}>
        <GraphViewer
          width={350}
          xRange={data.xRange}
          yRange={data.yRange}
          equations={[
            { expr: parabolaExpr, color: "#ff8c1a" },
            { expr: tangentExpr, color: "#9d4edd" },
          ]}
          points={{
            P: { x: data.x0, y: data.y0, label: "P", color: "#cc5500", textColor: "#cc5500", textPosition: "top center" },
          }}
        />
      </div>
    </div>
  );
}
